"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import useTranslations from "@/i18n/useTranslations";

type AllTabPagerProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

const baseClasses =
  "px-3 py-1 mr-0 text-sm hover:bg-secondary-400 hover:text-white";
const activeClasses = "bg-primary text-white border-double border-4";

const AllTabPager = ({ page, pageCount, onPageChange }: AllTabPagerProps) => {
  const t = useTranslations("Search");

  if (pageCount <= 1) return null;

  const isFirstPage = page === 0;
  const isLastPage = page === pageCount - 1;
  const pages = Array.from({ length: pageCount }, (_, index) => index);

  return (
    <nav aria-label={t("pagination")} className="mt-6 flex justify-center">
      <div className="flex flex-row items-center">
        <button
          type="button"
          className="p-2"
          onClick={() => onPageChange(page - 1)}
          disabled={isFirstPage}
          aria-label={t("previousPage")}
        >
          <ChevronLeftIcon
            className={`${isFirstPage ? "stroke-putty-dark" : "w-4 h-4 text-primary-500 bg-white stroke-primary-500 stroke-1 hover:rounded-full hover:bg-primary-500 hover:stroke-white"}`}
          />
        </button>

        <div className="flex flex-col items-end justify-end whitespace-nowrap text-sm md:hidden">
          <span>
            {" "}
            {t("pageOf", {
              current: page + 1,
              total: pageCount,
            })}{" "}
          </span>
        </div>
        <div className="hidden md:block">
          {pages.map((pageIndex, index) => (
            <button
              key={pageIndex}
              type="button"
              onClick={() => onPageChange(pageIndex)}
              className={`${baseClasses} ${page === pageIndex ? activeClasses : ""}`}
              aria-label={
                pages.length === index + 1
                  ? t("lastPage", { number: pageCount })
                  : t("page", { number: pageIndex + 1 })
              }
              aria-current={page === pageIndex ? "page" : "false"}
            >
              {pageIndex + 1}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="p-2"
          onClick={() => onPageChange(page + 1)}
          disabled={isLastPage}
          aria-label={t("nextPage")}
        >
          <ChevronRightIcon
            className={`${isLastPage ? "stroke-putty-dark" : "w-4 h-4 text-primary-500  stroke-primary-500 stroke-1 hover:rounded-full hover:bg-primary-500 hover:stroke-white"}`}
          />
        </button>
      </div>
    </nav>
  );
};

export default AllTabPager;
